import {emptyRelationTable} from '../relationTable';
import {specialBodyExtendedPagingOptions} from '../uiconstants';

/**
 * The state contract shared by the audit plan summary bodies (356 and 357).
 *
 * Four layers touch this state: the accounts reducer writes it, the assertion and risk reducers read
 * the same responses, the selectors read it back, and the actions tag the responses so the reducers
 * know a response belongs to these bodies. Putting the shapes and the discriminator here means none
 * of those layers has to import from another, and the empty shapes cannot drift apart.
 */

/**
 * Relation tables these bodies keep inside `state.accounts`.
 *
 * Only ids are stored. The account records live in `state.accounts.byId`, the assertions and risks in
 * the shared `_assertions` and `_risks` slices, and tasks exist only as edges, since a task covering
 * five assertions would otherwise be stored five times.
 */
export const emptyAuditPlanRelations = Object.freeze({
	accountAssertion: emptyRelationTable,
	assertionRisk: emptyRelationTable,
	accountTask: emptyRelationTable
});

/**
 * View state of a body that has not loaded yet.
 *
 * The reducer falls back to this when a body reports for the first time, and the selectors when a
 * body is asked about before its first response, so both must agree on every field.
 */
export const defaultAuditPlanBodyUi = Object.freeze({
	page: specialBodyExtendedPagingOptions.defaultPage,
	pageSize: specialBodyExtendedPagingOptions.defaultPageSize,
	pages: 0,
	totalEntityCount: 0,
	allIds: []
});

/**
 * Reads the discriminator carried by actions that belong to these bodies.
 * @param {Object} action Redux action.
 * @returns {Object | undefined} The discriminator when the action targets an audit plan summary body.
 */
export const getAuditPlanScope = (action) => action?.meta?.auditPlanSummary;

/**
 * Reads the accounts of an audit plan summary response as a list.
 *
 * The list endpoint answers with `{data: [...]}` and the detail endpoint with a single account, so
 * every reducer that flattens children out of these responses can treat both the same way.
 * @param {Object} action Redux action.
 * @returns {Array} The accounts carried by the response, empty when the action carries none.
 */
export function getAuditPlanAccounts(action) {
	if (!getAuditPlanScope(action)) return [];

	const response = action.response;
	if (Array.isArray(response?.data)) return response.data;
	return response?.id ? [response] : [];
}
